import React, { useContext, useState, useEffect } from 'react';
import { NetworkSetupTab } from './NetworkSetupTab';
import { ProductsTab } from './ProductsTab';
import { ConfigurationProvider, useConfigurationContext } from './Context/ConfigurationContext';
import { AvailabilityTab } from './AvailabilityTab';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { AppContext } from 'Context/AppContext';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import Button from 'Components/DesignSystem/Button/Button';
import Alert from 'Components/DesignSystem/Alert/Alert';
import { Cross } from 'Components/DesignSystem/Iconography/Icons';
import { SidePanelTabConfig } from 'types/DesignSystem/SidePanel';

type TabType = 'Network Setup' | 'Products' | 'Availability' | 'Related Products' | 'Orders' | 'Substitutes' | 'Price';

type ConfigurationPanelProps = {
    id?: string;
    setShowConfigurationPanel: (show: boolean) => void;
    callback?: () => void;
};

const CONFIGURATION_TABS: TabType[] = [
    'Network Setup',
    'Products',
    'Availability',
    // 'Related Products',
    // 'Orders',
    // 'Substitutes',
    // 'Price'
];

/** Tabs whose contents are mandatory before an import will run. */
const REQUIRED_TABS: TabType[] = ['Products', 'Availability'];

export const ConfigurationPanel = ({
    id = 'configuration-panel',
    setShowConfigurationPanel,
    callback
}: ConfigurationPanelProps) => {
    const appContext = useContext(AppContext);
    useEffect(() => {
        appContext?.setEditInProgress(true);
        return () => {
            appContext?.setEditInProgress(false);
        }
    }, [])
    return (
        <ConfigurationProvider>
            <ConfigurationPanelContent
                id={id}
                setShowConfigurationPanel={setShowConfigurationPanel}
                callback={callback}
            />
        </ConfigurationProvider>
    );
};

type ConfigurationPanelContentProps = {
    id: string;
    setShowConfigurationPanel: (show: boolean) => void;
    callback?: () => void;
};

/**
 * The connector Configuration drawer — SFTP credentials, product field mapping and the
 * availability format.
 *
 * On DS `SidePanel` since § 133. The gating logic is untouched: `isNetworkSetupComplete`
 * reads the same seven fields, `isTabEnabled` returns the same booleans, and the effect
 * that bounces you back to Network Setup when it becomes incomplete still fires.
 *
 * **It does NOT dismiss on a click beside it, while you are editing.** Every other
 * drawer in this module closes on a backdrop click, and this one does too — but only
 * out of edit mode. In edit mode the panel holds unsaved SFTP credentials and field
 * mappings, and mounting it sets `appContext.editInProgress`, which deliberately locks
 * the sidebar and the workspace switcher for exactly that reason. A stray click is not
 * an answer to "have you finished editing"; Cancel and Save are, and they are two
 * inches away in the header. Escape is refused on the same grounds.
 *
 * The chrome that went with the migration:
 *
 * - The hand-rolled tab strip (`cms-multi-tab-link`, `<button className="btn ...">`)
 *   is the panel's own tab row. It had no `role="tablist"`, no `role="tab"`, no
 *   `aria-selected` and no roving tabindex, so the three tabs were three unrelated
 *   buttons as far as assistive tech was concerned.
 * - The "Complete Network Setup before accessing other tabs" banner was a div with
 *   `tw-bg-[#F4F5F7]` and `tw-text-[#3D4966]` — two hardcoded hexes and no `role`, so
 *   it appeared and disappeared silently. DS `Alert` announces it.
 * - The close control was an `<img src={closeIcon}>` inside a Bootstrap button.
 */
const ConfigurationPanelContent = ({
    id,
    setShowConfigurationPanel
}: ConfigurationPanelContentProps) => {
    const [activeTab, setActiveTab] = useState<TabType>('Network Setup');
    const { isEditMode, setIsEditMode, saveConfiguration, currentConfig, cancelEdit, isLoading } = useConfigurationContext();
    const appData = useContext(AppContext);

    const ns = currentConfig?.network_setup;
    const hasValidEmail = !ns?.partial_processing ||
        (Array.isArray(ns?.email_ids) && ns.email_ids.some((e: string) => e?.trim()));
    const isNetworkSetupComplete = !!(
        ns?.import_type?.trim() &&
        ns?.sftp_host?.trim() &&
        ns?.sftp_username?.trim() &&
        ns?.sftp_password?.trim() &&
        ns?.port &&
        ns?.file_location?.trim() &&
        hasValidEmail
    );

    const isTabEnabled = (tab: TabType): boolean => {
        if (tab !== 'Network Setup' && !isNetworkSetupComplete) { return false }
        switch (tab) {
            case 'Network Setup':
                return true;
            case 'Products':
                return true;
            case 'Availability':
                return true;
            case 'Related Products':
            case 'Orders':
            case 'Substitutes':
            case 'Price':
                return false;
            default:
                return false;
        }
    };

    useEffect(() => {
        if (activeTab !== 'Network Setup' && !isNetworkSetupComplete) {
            setActiveTab('Network Setup');
        }
    }, [isNetworkSetupComplete, activeTab]);

    const handleCancel = () => {
        cancelEdit();
        setShowConfigurationPanel(false);
    };

    const handleSave = async () => {
        const success = await saveConfiguration();
        if (success) {
            setShowConfigurationPanel(false);
        }
    };

    /**
     * Backdrop click and Escape both land here. See the component docblock for why
     * edit mode refuses them — and why it says so rather than doing nothing, which
     * would read as the panel being broken.
     */
    const handleDismiss = () => {
        if (isEditMode) {
            toast.info('Finish editing first — use Cancel or Save to close the configuration.');
            return;
        }
        setIsEditMode(false);
        setShowConfigurationPanel(false);
    };

    const handleTabChange = (tab: TabType) => {
        if (isTabEnabled(tab)) {
            setActiveTab(tab);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'Network Setup':
                return <NetworkSetupTab id={`${id}-network-setup`} />;
            case 'Products':
                return <ProductsTab id={`${id}-products`} />;
            case 'Availability':
                return <AvailabilityTab id={`${id}-availability`} />;
            default:
                return null;
        }
    };

    const tabs: SidePanelTabConfig<TabType>[] = CONFIGURATION_TABS.map((tab) => ({
        id: tab,
        label: tab,
        disabled: !isTabEnabled(tab),
        /* The tab ids are display strings (`Network Setup`), so the testid is slugged
           explicitly rather than derived — `configuration-panel-tab-Network Setup` is
           not a selector anyone wants to write. Unchanged from the legacy scheme. */
        'data-testid': `${id}-tab-${tab.toLowerCase().replace(/\s+/g, '-')}`,
        /* The required marker, previously a bare `<span className="tw-text-red-500">*</span>`
           appended inside the button. `Tab` draws it against the label now, in the same
           tone `FieldTitle` uses, and `aria-hidden` — "required" is not carried by a
           glyph; the Alert says it in words and the fields themselves are marked. */
        required: REQUIRED_TABS.includes(tab),
    }));

    return (
        <SidePanel
            isOpen
            onClose={handleDismiss}
            title="Configuration"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            width="lg"
            data-testid={id}
            headerActions={
                isEditMode ? (
                    <div className="tw-flex tw-gap-2" data-testid={`${id}-edit-actions`}>
                        <Button
                            variant="secondary"
                            onClick={handleCancel}
                            data-testid={`${id}-cancel-btn`}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            data-testid={`${id}-save-btn`}
                        >
                            Save
                        </Button>
                    </div>
                ) : (
                    <div className="tw-flex tw-items-center tw-gap-2" data-testid={`${id}-view-actions`}>
                        <Button
                            variant="secondary"
                            data-testid={`${id}-edit-btn`}
                            disabled={!appData.pm.checkPermission("gmsconnector_config")}
                            onClick={() => setIsEditMode(true)}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="tertiary"
                            size="sm"
                            aria-label="Close configuration"
                            data-testid={`${id}-close-btn`}
                            onClick={() => {
                                setIsEditMode(false);
                                setShowConfigurationPanel(false);
                            }}
                        >
                            <Cross size={16} />
                        </Button>
                    </div>
                )
            }
        >
            {!isLoading && !isNetworkSetupComplete && (
                <Alert tone="info" className="tw-mb-4" data-testid={`${id}-incomplete-alert`}>
                    Complete Network Setup before accessing other tabs
                </Alert>
            )}

            <div data-testid={`${id}-content`}>
                {renderTabContent()}
            </div>
        </SidePanel>
    );
};

export default ConfigurationPanel;
